import React, { FC, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Select from "@/components/Select/Select";
import { useUserStore } from "@/hooks/useUserStore";
import { useGetUserPrograms } from "@/hooks/usePrograms";
import { SkeletonClientFilters } from "@/ui/menus/clients/Skeletons";
import { useOrganizationUnitsByIds } from "@/hooks/useOrganizations";
import { Program } from "@/domain/programs.interface";
import { Organization } from "@/domain/organizations.interface";

export type Filters = {
  sortField: string;
  sortOrder: number;
  pageNumber: number;
  pageSize: number;
  search: string;
  status: string[];
  programId: string;
  orgUnitId: string;
  orgGroupLabels: string[];
  excludeFromProgramsAndOrgUnits: boolean;
  excludeProgramIds: string[];
};

type Props = {
  filtersState: [Filters, React.Dispatch<React.SetStateAction<Filters>>];
  isLoadingButtons: boolean;
  onOpenAuditLog: () => void;
};

const HeaderFilters: FC<Props> = ({ filtersState }) => {
  const [filters, setFilters] = filtersState;
  const { userInfo, userIsAdminOrSupport } = useUserStore();
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [selectedOrganizationUnit, setSelectedOrganizationUnit] =
    useState<Organization | null>(null);
  const [selectedOrgUnits, setSelectedOrgUnits] = useState<string[]>([]);
  const [selectedGroupLabels, setSelectedGroupLabels] = useState<string[]>([]);

  const isAdminOrSupport = userIsAdminOrSupport();

  const { programs, isLoadingPrograms, isFetchingPrograms } =
    useGetUserPrograms({
      userId: userInfo?._id as string,
      sortType: "asc",
      hasSystemAdminRole: isAdminOrSupport,
    });

  const programsOptions = useMemo(() => {
    if (!programs) return [];

    // Sort programs alphabetically by name
    const sortedPrograms = [...programs].sort((a, b) =>
      a.name.localeCompare(b.name),
    );

    const options = sortedPrograms.map((program) => ({
      key: program._id,
      value: program.name,
    }));

    // Add "All programs" option at the top if more than one program
    if (options.length > 1) {
      return [{ key: "", value: "All programs" }, ...options];
    }

    return options;
  }, [programs]);

  useEffect(() => {
    if (programs && programs.length > 0 && !filters.programId) {
      // Sort programs alphabetically
      const sortedPrograms = [...programs].sort((a, b) =>
        a.name.localeCompare(b.name),
      );

      // Default: First program after "All programs" option
      // If only one program, use it; if multiple, use first sorted program
      const defaultProgram = sortedPrograms[0];

      setFilters((prevFilters) => ({
        ...prevFilters,
        programId: defaultProgram._id,
      }));

      setSelectedProgram(defaultProgram);
    }
  }, [programs, filters.programId, setFilters]);

  useEffect(() => {
    if (programs && programs.length > 0 && filters.programId) {
      const program = programs.find((p) => p._id === filters.programId);
      if (
        program &&
        (!selectedProgram || selectedProgram._id !== program._id)
      ) {
        setSelectedProgram(program);
      }
    }
  }, [programs, filters.programId, selectedProgram]);

  const hasOrgUnits = useMemo(() => {
    return !!selectedProgram && (selectedProgram?.orgUnitIds?.length ?? 0) > 0;
  }, [selectedProgram]);

  const orgUnitIds = useMemo(() => {
    return hasOrgUnits && selectedProgram?.orgUnitIds
      ? selectedProgram.orgUnitIds
      : ["placeholder-id"];
  }, [hasOrgUnits, selectedProgram]);

  const {
    organizationUnits,
    isLoadingOrganizationUnits,
    refetchOrganizationUnits,
  } = useOrganizationUnitsByIds({
    ids: orgUnitIds,
    status: "Active",
    enabled: hasOrgUnits,
  });

  const organizationUnitsOptions = useMemo(() => {
    if (!organizationUnits || !organizationUnits.organizations) return [];

    // Sort OUs alphabetically by name
    const sortedOrgUnits = [...organizationUnits.organizations].sort((a, b) =>
      a.name.localeCompare(b.name),
    );

    return sortedOrgUnits.map((orgUnit) => ({
      key: orgUnit._id,
      value: orgUnit.name,
      groupLabelIds: orgUnit.groupLabels?.map((gl) => gl._id) || [],
    }));
  }, [organizationUnits]);

  const organizationGroupLabelsOptions = useMemo(() => {
    if (!organizationUnits) return [];

    const allGroupLabels = organizationUnits.organizations
      .map((orgUnit) => orgUnit.groupLabels)
      .flat();

    const uniqueGroupLabelsMap = new Map();

    allGroupLabels.forEach((groupLabel) => {
      if (!uniqueGroupLabelsMap.has(groupLabel._id)) {
        uniqueGroupLabelsMap.set(groupLabel._id, {
          key: groupLabel._id,
          value: groupLabel.name,
        });
      }
    });

    // Sort OU Labels alphabetically by name
    const sortedLabels = Array.from(uniqueGroupLabelsMap.values()).sort(
      (a, b) => a.value.localeCompare(b.value),
    );

    return sortedLabels;
  }, [organizationUnits]);

  useEffect(() => {
    if (selectedProgram) {
      refetchOrganizationUnits();
    }
  }, [selectedProgram, refetchOrganizationUnits]);

  // Auto-select all OUs when organizationUnitsOptions changes and nothing is selected
  useEffect(() => {
    const allOuIds = organizationUnitsOptions.map((ou) => ou.key);
    const validSelected = selectedOrgUnits.filter((id) =>
      allOuIds.includes(id),
    );

    if (allOuIds.length > 0 && validSelected.length === 0) {
      setSelectedOrgUnits(allOuIds);
      setFilters((filters) => ({
        ...filters,
        orgUnitId: allOuIds.join(","),
      }));
    }
  }, [organizationUnitsOptions]);

  return (
    <Box
      sx={{
        display: "flex",
        gap: "1.25rem",
        width: "100%",
        flexDirection: {
          xs: "column",
          sm: "column",
          md: "column",
        },
      }}
    >
      {isFetchingPrograms || isLoadingPrograms ? (
        <SkeletonClientFilters />
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              gap: "0.5rem",
              width: {
                xs: "100%",
                sm: "100%",
              },
              flexDirection: {
                xs: "column",
                sm: "row",
                md: "row",
              },
            }}
          >
            <Select
              handleChange={(value) => {
                setFilters((filters) => ({
                  ...filters,
                  programId: value as string,
                  orgUnitId: "",
                  orgGroupLabels: [],
                  pageNumber: 0,
                }));

                setSelectedOrgUnits([]);
                setSelectedGroupLabels([]);

                if (value) {
                  setSelectedProgram(
                    programs?.find(
                      (program) => program._id === value,
                    ) as Program,
                  );

                  setSelectedOrganizationUnit(null);
                } else {
                  // "All programs" selected
                  setSelectedProgram(null);
                }
              }}
              data={programsOptions || []}
              value={filters.programId}
              label="Program"
              id="users-program"
              isLoading={isLoadingPrograms}
              disabled={filters.excludeFromProgramsAndOrgUnits}
              maxWidth={320}
              fullWidth
              backgroundColor={"white"}
            />

            <Select
              handleChange={(value) => {
                let selected = Array.isArray(value) ? value : [value];

                // If nothing selected, select all
                if (
                  selected.length === 0 &&
                  organizationUnitsOptions.length > 0
                ) {
                  selected = organizationUnitsOptions.map((ou) => ou.key);
                }

                setSelectedOrgUnits(selected);
                setSelectedGroupLabels([]); // Clear group labels when OU is selected

                setFilters((filters) => ({
                  ...filters,
                  orgUnitId: selected.join(","),
                  orgGroupLabels: [],
                  pageNumber: 0,
                }));

                setSelectedOrganizationUnit(null);
              }}
              data={organizationUnitsOptions || []}
              value={selectedOrgUnits}
              label="Organization Unit"
              id="organization-unit"
              multiple
              isLoading={isLoadingOrganizationUnits || selectedProgram === null}
              disabled={
                filters.excludeFromProgramsAndOrgUnits || !filters.programId // Disabled when "All programs" is selected
              }
              maxWidth={320}
              fullWidth
              backgroundColor={"white"}
            />

            <Select
              handleChange={(value) => {
                const selectedLabelIds = Array.isArray(value) ? value : [value];
                setSelectedGroupLabels(selectedLabelIds);

                let selectedOuIds: string[];
                if (selectedLabelIds.length === 0) {
                  // No labels selected: select all OUs
                  selectedOuIds = organizationUnitsOptions.map((ou) => ou.key);
                } else {
                  // Find OUs with any of the selected group labels
                  const matchingOUs = organizationUnitsOptions.filter(
                    (ou) =>
                      Array.isArray(ou.groupLabelIds) &&
                      ou.groupLabelIds.some((labelId) =>
                        selectedLabelIds.includes(labelId),
                      ),
                  );
                  selectedOuIds = matchingOUs.map((ou) => ou.key);
                }

                setSelectedOrgUnits(selectedOuIds);
                setFilters((filters) => ({
                  ...filters,
                  orgGroupLabels: selectedLabelIds,
                  orgUnitId: selectedOuIds.join(","),
                  pageNumber: 0,
                }));
              }}
              data={organizationGroupLabelsOptions || []}
              value={selectedGroupLabels}
              label="Group Labels"
              id="group-labels"
              multiple
              maxWidth={320}
              fullWidth
              disabled={
                filters.excludeFromProgramsAndOrgUnits || !filters.programId // Disabled when "All programs" is selected
              }
              isLoading={
                isLoadingOrganizationUnits ||
                organizationGroupLabelsOptions.length === 0
              }
              backgroundColor={"white"}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default HeaderFilters;
